import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalFooter } from '@modals/core-ui/ModalFooter';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { pluralize } from '@helpers/pluralize';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { JobsService } from 'app/API';
import { useState } from 'react';
import toast from 'react-hot-toast';

type TProps = {
  jobIds: string[];
  onCancel: () => void;
  onSuccess: () => void;
};

export const DeleteJobsModal = ({ onCancel, onSuccess, jobIds }: TProps) => {
  const { workspace } = useAuthContext();
  const { call } = useAPI();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    let requestErrorMessage: string | undefined;
    try {
      await call(
        () =>
          JobsService.softDeleteJobs({
            wsid: workspace?.id ?? '',
            requestBody: { jobIds },
          }),
        {
          dontRedirectOnError: true,
          onError: (message) => {
            requestErrorMessage = message;
          },
        },
      );
      onSuccess();
      onCancel();
    } catch {
      toast.error(requestErrorMessage ?? 'Unable to delete the selected jobs. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper width="400px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Confirm</ModalTitle>
      <p className="m-0 text-center">
        Are you sure you want to delete {pluralize(jobIds.length, 'this job', `these ${jobIds.length} jobs`)}?
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <ModalFooter>
          <OutlinedButton type="button" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </OutlinedButton>
          <Button type="submit" disabled={isSubmitting}>
            Delete
          </Button>
        </ModalFooter>
      </form>
    </ModalWrapper>
  );
};
